import React from 'react';
import {
    Document, Page, Text, View, StyleSheet, Image, Font
} from '@react-pdf/renderer';
import { formatIsoDateToItalian as formatDate } from '../utils/utils';

// Numero di pagine con ancora firma OTP, deve coincidere con i F(n) del documento.
// Non va limitato al conteggio reale delle pagine: ancore mancanti danno 422 su OTP.
export const MANDATO_FOOTER_PAGES = 12;

/* Palette */
const C = {
    black:     '#1a1a1a',
    darkGray:  '#333333',
    midGray:   '#666666',
    lightGray: '#f0f0f0',
    silver:    '#cccccc',
    accent:    '#8b0000',
    accentLight: '#f9eeee',
    headerBg:  '#1c2b4a',
    sectionBg: '#dde3ec',
    sectionBorder: '#1c2b4a',
    fieldLine: '#888888',
    white:     '#ffffff',
    sigBorder: '#d32f2f', // bordo rosso dei riquadri firma cliente
};

/* Stili */
const styles = StyleSheet.create({
    page: {
        fontSize: 9,
        fontFamily: 'Times-Roman',
        lineHeight: 1.3,
        color: C.black,
        // Spazio per il numero di pagina fisso, così non si sovrappone al footer con la
        // casella firma (che non è fixed e va in fondo con marginTop: 'auto').
        paddingBottom: 18,
    },
    header: {
        paddingTop: 0,
        paddingBottom: 2,
        paddingHorizontal: 30,
    },
    headerBanner: {
        width: '100%',
        height: 75,
        // contain e non cover: l'header di solito ha logo e contatti sotto, che cover taglierebbe.
        // È lo stesso comportamento dell'anteprima in Impostazioni.
        objectFit: 'contain',
    },
    headerInner: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 8,
        paddingBottom: 6,
    },
    logo: {
        width: 65,
        height: 55,
        objectFit: 'contain',
    },
    headerTextBlock: {
        flex: 1,
        paddingLeft: 10,
    },
    headerAgency: {
        fontSize: 11,
        fontFamily: 'Times-Roman',
        color: C.black,
        letterSpacing: 0.3,
    },
    headerSub: {
        fontSize: 7,
        color: C.midGray,
        marginTop: 1,
    },
    headerTitleBox: {
        borderWidth: 1,
        borderColor: C.darkGray,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginTop: 2,
        marginBottom: 2,
        backgroundColor: C.white,
    },
    headerTitleConfidentialWrap: {
        alignItems: 'center',
        marginBottom: 2,
    },
    headerTitleConfidential: {
        fontSize: 9,
        fontFamily: 'Times-Roman',
        textAlign: 'center',
        color: C.black,
        borderBottomWidth: 0.5,
        borderBottomColor: C.black,
        paddingBottom: 1,
    },
    headerTitleMain: {
        fontSize: 11,
        fontFamily: 'Times-Roman',
        textAlign: 'center',
        color: C.black,
        letterSpacing: 0.3,
        marginBottom: 2,
    },
    headerTitleSub: {
        fontSize: 7,
        textAlign: 'center',
        color: C.midGray,
        fontFamily: 'Times-Italic',
    },
    headerMeta: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        paddingTop: 2,
        paddingBottom: 1,
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
        marginTop: 2,
    },
    headerPageNum: {
        fontSize: 7,
        color: C.midGray,
        textAlign: 'right',
    },

    // Footer non fixed, spinto in fondo con marginTop: 'auto' come in BonificaPDF/ConsulenzaPDF.
    // Con position fixed OTP trovava l'ancora #otp_mf{n}# in alto invece che nella casella.
    footer: {
        marginTop: 'auto',
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
        paddingHorizontal: 30,
        paddingVertical: 5 ,
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    footerSignature: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginLeft: 16,
        minWidth: 130,
    },
    footerSignatureLabel: {
        fontSize: 7,
        color: C.midGray,
        marginBottom: 2,
    },
    footerSignatureLine: {
        width: 130,
        height: 20,
        borderWidth: 0.75,
        borderColor: C.sigBorder,
        backgroundColor: '#fafafa',
    },
    footerCopyright: {
        fontSize: 6,
        color: C.midGray,
        fontFamily: 'Times-Italic',
        textAlign: 'left',
        lineHeight: 1.4,
        flex: 1,
    },
    // Numero pagina fisso, centrato in basso e ben separato dalla casella firma
    // (in basso a destra) per non confonderlo con l'ancora OTP.
    footerPageNum: {
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888888',
    },

    boxSectionTitle: {
        borderWidth: 1,
        borderColor: C.darkGray,
        backgroundColor: C.lightGray,
        paddingVertical: 3,
        paddingHorizontal: 6,
        marginTop: 6,
        marginBottom: 4,
        textAlign: 'center',
        fontSize: 8.5,
        fontFamily: 'Times-Roman',
        color: C.black,
    },

    sectionTitle: {
        backgroundColor: C.sectionBg,
        borderLeftWidth: 3,
        borderLeftColor: C.sectionBorder,
        paddingVertical: 3,
        paddingHorizontal: 6,
        marginTop: 7,
        marginBottom: 3,
        fontSize: 8.5,
        fontFamily: 'Times-Roman',
        color: C.headerBg,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },

    paragraph: {
        marginBottom: 3,
        textAlign: 'justify',
        fontSize: 9,
        lineHeight: 1.3,
        color: C.black,
    },

    fieldRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 3,
    },
    fieldLabel: {
        fontFamily: 'Times-Roman',
        fontSize: 8.5,
        color: C.darkGray,
        marginRight: 4,
        whiteSpace: 'nowrap',
    },
    fieldValue: {
        flex: 1,
        borderBottomWidth: 0.75,
        borderBottomColor: C.fieldLine,
        borderBottomStyle: 'solid',
        fontSize: 9,
        paddingBottom: 1,
        minHeight: 12,
        color: C.black,
    },

    checkRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginRight: 12,
        marginBottom: 2,
    },
    checkbox: {
        width: 10,
        height: 10,
        borderWidth: 1,
        borderColor: C.darkGray,
        borderRadius: 1,
        marginRight: 4,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: C.white,
    },
    checkLabel: {
        fontSize: 9,
        color: C.black,
    },

    textBox: {
        borderWidth: 0.75,
        borderColor: C.silver,
        backgroundColor: '#fafafa',
        minHeight: 14,
        marginBottom: 5,
        padding: 4,
    },
    textBoxContent: {
        fontSize: 9,
        color: C.midGray,
    },

    tariffeRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 3,
        paddingLeft: 6,
    },
    tariffeLabel: {
        fontSize: 9,
        width: 200,
        color: C.black,
    },
    tariffeValue: {
        fontSize: 9,
        flex: 1,
        borderBottomWidth: 0.75,
        borderBottomColor: C.fieldLine,
        paddingBottom: 1,
        minHeight: 12,
    },

    signatureLabel: {
        fontSize: 9,
        fontFamily: 'Times-Italic',
        marginBottom: 3,
        color: C.darkGray,
        textAlign: 'left',
    },
    signatureArrowRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    signatureArrow: {
        fontSize: 12,
        color: C.accent,
        marginRight: 5,
    },
    signatureLine: {
        flex: 1,
        borderWidth: 0.75,
        borderColor: C.sigBorder,
        backgroundColor: '#fafafa',
        height: 45,
    },

    noteBox: {
        borderLeftWidth: 2,
        borderLeftColor: C.accent,
        backgroundColor: '#fffbf0',
        paddingLeft: 6,
        paddingVertical: 3,
        paddingRight: 4,
        marginVertical: 3,
    },
    noteText: {
        fontSize: 8.5,
        color: C.darkGray,
        fontFamily: 'Times-Italic',
    },

    divider: {
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
        marginVertical: 5,
    },

    clausoleBox: {
        borderWidth: 1,
        borderColor: C.sectionBorder,
        backgroundColor: '#f0f4fa',
        padding: 7,
        marginVertical: 5,
    },
    clausoleText: {
        fontSize: 9,
        fontFamily: 'Times-Roman',
        textAlign: 'justify',
        lineHeight: 1.3,
    },

    allegatiBox: {
        borderWidth: 1,
        borderColor: C.darkGray,
        marginVertical: 4,
    },
    allegatiItem: {
        fontSize: 9,
        paddingVertical: 3,
        paddingHorizontal: 6,
        borderBottomWidth: 0.5,
        borderBottomColor: C.silver,
    },
    allegatiItemLast: {
        fontSize: 9,
        paddingVertical: 3,
        paddingHorizontal: 6,
    },

    writeLine: {
        borderBottomWidth: 0.5,
        borderBottomColor: C.silver,
        marginVertical: 6,
        height: 1,
    },
});

/* Helper */

// Le date vanno in formato italiano DD/MM/YYYY (formatIsoDateToItalian da utils).

const Field = ({ label, value, style }: { label: string; value?: any; style?: any }) => (
    <View style={[styles.fieldRow, style]}>
        <Text style={styles.fieldLabel}>{label}</Text>
        <Text style={styles.fieldValue}>{value || ''}</Text>
    </View>
);

const Check = ({ checked, label }: { checked?: any; label: string }) => (
    <View style={styles.checkRow}>
        <View style={styles.checkbox}>
            {checked ? <View style={{ width: 7, height: 7, backgroundColor: C.accent }} /> : null}
        </View>
        <Text style={styles.checkLabel}>{label}</Text>
    </View>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Text style={styles.sectionTitle}>{children}</Text>
);

const BoxTitle = ({ children, italic }: { children: React.ReactNode; italic?: boolean }) => (
    <Text style={[styles.boxSectionTitle, italic ? { fontFamily: 'Times-Italic' } : {}]}>{children}</Text>
);

const Para = ({ children, bold, underline, italic, style }: any) => {
    if (bold && underline) {
        return (
            <View style={{ marginBottom: 4, borderBottomWidth: 0.5, borderBottomColor: C.black, paddingBottom: 1 }}>
                <Text style={[styles.paragraph, { fontFamily: 'Times-Bold', marginBottom: 0 }, style]}>
                    {children}
                </Text>
            </View>
        );
    }
    if (underline && !bold) {
        return (
            <View style={{ marginBottom: 4, borderBottomWidth: 0.5, borderBottomColor: C.darkGray, paddingBottom: 1 }}>
                <Text style={[styles.paragraph, { marginBottom: 0 }, style]}>
                    {children}
                </Text>
            </View>
        );
    }
    return (
        <Text style={[
            styles.paragraph,
            bold && italic ? { fontFamily: 'Times-BoldItalic' } :
            bold ? { fontFamily: 'Times-Bold' } :
            italic ? { fontFamily: 'Times-Italic' } : {},
            italic ? { color: C.midGray } : {},
            style,
        ]}>
            {children}
        </Text>
    );
};

const TextBox = ({ value, minHeight }: { value?: string; minHeight?: number }) => (
    <View style={[styles.textBox, minHeight ? { minHeight } : {}]}>
        <Text style={styles.textBoxContent}>{value || ''}</Text>
    </View>
);

const NoteBox = ({ children }: { children: any }) => (
    <View style={styles.noteBox}>
        <Text style={styles.noteText}>{children}</Text>
    </View>
);

const isSupportedImageUrl = (url?: string) =>
    !!url && !url.startsWith('data:image/svg');

const PageHeader = ({ settings, data }: any) => {
    const rawBanner = settings?.mandatoHeaderUrl || settings?.mandatoHeaderImageUrl || settings?.reportHeaderUrl;
    const bannerUrl = isSupportedImageUrl(rawBanner) ? rawBanner : undefined;
    const rawLogo = settings?.logoUrl || data?.logoUrl;
    const logoUrl = isSupportedImageUrl(rawLogo) ? rawLogo : undefined;
    const agencyName = settings?.agencyName || data?.nome_azienda || '';

    return (
        <View style={styles.header} fixed>
            {bannerUrl ? (
                <Image src={bannerUrl} style={styles.headerBanner} />
            ) : (
                <View style={styles.headerInner}>
                    {logoUrl && <Image src={logoUrl} style={styles.logo} />}
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerAgency}>{agencyName}</Text>
                        <Text style={styles.headerSub}>Investigazioni Private · Sicurezza · Consulenza</Text>
                    </View>
                </View>
            )}
            <View style={styles.headerTitleBox}>
                <View style={styles.headerTitleConfidentialWrap}>
                    <Text style={styles.headerTitleConfidential}>DOCUMENTO SOTTOPOSTO A VINCOLO DI RISERVATEZZA</Text>
                </View>
                <Text style={styles.headerTitleMain}>CONFERIMENTO INCARICO PER INVESTIGAZIONI PRIVATE</Text>
                <Text style={styles.headerTitleSub}>
                    Ai sensi degli art. 134 e ss. del R.D. 18 giugno 1931 nr. 773 - T.U.L.P.S., del D.P.R. 4 Agosto 2008, n. 153, del D.M. 1 Dicembre 2010 n. 269, Del Regolamento (UE) 2016/679 e delle Regole Deontologiche del 19 dicembre 2018
                </Text>
            </View>
            <View style={styles.headerMeta}>
                <Text style={styles.headerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`}
                />
            </View>
        </View>
    );
};

/* Footer con casella firma in basso a destra, come in BonificaPDF/ConsulenzaPDF.
   pageNum arriva dal chiamante: con un fixed View e `render` OTP trovava l'ancora
   in alto invece che nella casella. */
const PageFooter = ({ pageNum }: { pageNum: number }) => (
    <View style={styles.footer}>
        <Text style={styles.footerCopyright}>
            Il presente documento costituisce opera dell'ingegno ai sensi della normativa vigente ed è riservato all'uso esclusivo dei soggetti espressamente autorizzati dall'autore. È vietata qualsiasi forma di riproduzione, comunicazione, diffusione o utilizzazione, anche parziale, in assenza di preventiva autorizzazione scritta.
        </Text>
        <View style={styles.footerSignature}>
            <Text style={styles.footerSignatureLabel}>Firma Mandante:</Text>
            <View style={styles.footerSignatureLine}>
                <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{`#otp_mf${pageNum}#`}</Text>
            </View>
        </View>
    </View>
);

/* Documento */

export const MandatoPDF = ({ data }: { data: any }) => {
    const { cliente, pratica, settings } = data;
    // Modulo in bianco da compilare a penna: nessuna casella spuntata in automatico.
    const inBianco = !!data.inBianco;
    const allDelegati: any[] = (data.delegati?.length > 0 ? data.delegati : pratica?.delegati_lista) || [];
    const delegati = [allDelegati[0] || {}, allDelegati[1] || {}];
    const safeSettings = settings || {};

    const isAzienda = cliente?.tipo === 'Azienda';
    // Il mandante è sempre la persona fisica che firma: il cliente se privato,
    // il referente se azienda (la ragione sociale va nei dati soggetto terzi).
    const nomeMandante = `${cliente?.nome || ''} ${cliente?.surname || ''}`.trim();
    const nomeAzienda = data.nome_azienda || '';
    const cs = { paddingHorizontal: 30 };
    // Il footer va in coda a ogni pagina con il suo pageNum, per generare l'ancora #otp_mf{n}#.
    const F = (n: number) => <PageFooter pageNum={n} />;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                {/* Data e Rif */}
                <View style={[styles.fieldRow, { marginTop: 10 }]}>
                    <Text style={[styles.fieldLabel, { width: 185 }]}>DATA SOTTOSCRIZIONE INCARICO</Text>
                    <Text style={[styles.fieldValue, { flex: 1.4 }]}>{formatDate(pratica?.data_incarico)}</Text>
                    <Text style={[styles.fieldLabel, { marginLeft: 18 }]}>RIF. N°</Text>
                    <Text style={[styles.fieldValue, { flex: 0.6 }]}>
                        {pratica && !pratica.rif_numero?.toLowerCase().startsWith('temp') ? pratica.rif_numero : ''}
                    </Text>
                </View>

                {/* Dati mandante */}
                <BoxTitle>IL PRESENTE CONFERIMENTO DI INCARICO VIENE SOTTOSCRITTO DA:{' '}( di seguito "mandante" )</BoxTitle>
                <Field label="COGNOME E NOME:" value={nomeMandante} />
                <View style={styles.fieldRow}>
                    <Field label="LUOGO DI NASCITA:" value={cliente?.luogoNascita} style={{ flex: 2 }} />
                    <Field label="IN DATA:" value={formatDate(cliente?.dataNascita)} style={{ flex: 1 }} />
                </View>
                {(() => {
                    // Per un'azienda i campi nativi sono la sede legale: la residenza del mandante
                    // sta nei campi *Referente. Per un privato si usano i campi nativi.
                    const indMandante = isAzienda ? (cliente?.indirizzoReferente || cliente?.indirizzo) : cliente?.indirizzo;
                    const capMandante = isAzienda ? (cliente?.capReferente || cliente?.cap) : cliente?.cap;
                    const cittaMandante = isAzienda ? (cliente?.cittaReferente || cliente?.citta) : cliente?.citta;
                    const provMandante = isAzienda ? (cliente?.provinciaReferente || cliente?.provincia) : cliente?.provincia;
                    return (
                        <>
                            <View style={styles.fieldRow}>
                                <Field label="RESIDENTE IN VIA:" value={indMandante} style={{ flex: 2 }} />
                                <Field label="C.A.P:" value={capMandante} style={{ flex: 0.8 }} />
                            </View>
                            <View style={styles.fieldRow}>
                                <Field label="COMUNE:" value={cittaMandante} style={{ flex: 2 }} />
                                <Field label="PROV:" value={provMandante} style={{ flex: 0.6 }} />
                            </View>
                        </>
                    );
                })()}
                <Field label="CODICE FISCALE:" value={cliente?.codiceFiscale} />

                {/* Identificazione */}
                <Para bold>
                    IDENTIFICATO AI SENSI DELL'ART. 135 DEL T.U.L.P.S., della normativa vigente, delle disposizioni applicabili in materia di pubblica sicurezza, protezione dei dati personali e rapporti contrattuali, mediante esibizione di valido documento di riconoscimento.
                </Para>
                <View style={styles.fieldRow}>
                    <Field label="TIPOLOGIA DOC. ESIBITO:" value={cliente?.docTipo} style={{ flex: 1 }} />
                    <Field label="NUMERO:" value={cliente?.docNumero} style={{ flex: 1 }} />
                </View>
                <View style={styles.fieldRow}>
                    <Field label="RILASCIATO IL:" value={formatDate(cliente?.docDataRilascio)} style={{ flex: 1 }} />
                    <Field label="DA:" value={cliente?.docRilascioDa} style={{ flex: 1 }} />
                </View>
                <Field label="VALIDO FINO AL:" value={formatDate(cliente?.docDataScadenza || cliente?.docValidoFinoAl)} />

                {/* Agisce per conto: per un'azienda il referente firma per conto di questa,
                    quindi "DI:" riporta la ragione sociale. */}
                <BoxTitle>AGISCE IN NOME E PER CONTO</BoxTitle>
                {(() => {
                    // Azienda: sempre "per conto di" l'azienda, salvo perContoDi impostato a mano.
                    const perContoLabel = isAzienda
                        ? (cliente?.perContoDi
                            || cliente?.ragioneSociale
                            || cliente?.denominazioneTerzi
                            || cliente?.nome
                            || '')
                        : (cliente?.perContoDi || '');
                    const isProprio = !isAzienda && !cliente?.perContoDi;
                    return (
                        <View style={[styles.fieldRow, { alignItems: 'center' }]}>
                            <Check checked={!inBianco && isProprio} label="PROPRIO:" />
                            <View style={{ width: 20 }} />
                            <Text style={styles.fieldLabel}>DI:</Text>
                            <Text style={styles.fieldValue}>{perContoLabel}</Text>
                        </View>
                    );
                })()}
                <Para bold>
                    In caso di conferimento del mandato in nome e per conto di terzi, il sottoscrittore dichiara, sotto la propria responsabilità, di agire in qualità di Titolare, Legale Rappresentante, procuratore o soggetto formalmente da questi delegato, e di essere munito dei necessari poteri di rappresentanza ai fini del presente incarico, assumendosene ogni conseguente responsabilità civile e penale.
                </Para>

                {/* Dati soggetto terzi: per un'azienda dai campi nativi del cliente, con ripiego
                    sui campi "per conto di terzi" dei record vecchi. */}
                <BoxTitle italic>
                    Dati del soggetto (persona fisica / persona giuridica / ente / professionista) per conto del quale il Mandante agisce
                </BoxTitle>
                {(() => {
                    const denominazione = isAzienda
                        ? (cliente?.ragioneSociale || cliente?.denominazioneTerzi)
                        : cliente?.denominazioneTerzi;
                    const sede = isAzienda
                        ? (cliente?.indirizzo || cliente?.sedeTerzi)
                        : cliente?.sedeTerzi;
                    const cap = isAzienda
                        ? (cliente?.cap || cliente?.capTerzi)
                        : cliente?.capTerzi;
                    const comune = isAzienda
                        ? (cliente?.citta || cliente?.comuneTerzi)
                        : cliente?.comuneTerzi;
                    const prov = isAzienda
                        ? (cliente?.provincia || cliente?.provinciaTerzi)
                        : cliente?.provinciaTerzi;
                    const piva = isAzienda
                        ? (cliente?.partitaIva || cliente?.pivaOrCfTerzi)
                        : cliente?.pivaOrCfTerzi;
                    const sdi = isAzienda
                        ? (cliente?.codiceSdi || cliente?.codiceSdiTerzi)
                        : cliente?.codiceSdiTerzi;
                    const pec = isAzienda
                        ? (cliente?.pec || cliente?.emailTerzi)
                        : cliente?.emailTerzi;
                    return (
                        <>
                            <Field label="DENOMINAZIONE / NOME E COGNOME:" value={denominazione} />
                            <View style={styles.fieldRow}>
                                <Field label="SEDE / RESIDENZA IN VIA:" value={sede} style={{ flex: 2 }} />
                                <Field label="C.A.P:" value={cap} style={{ flex: 0.8 }} />
                            </View>
                            <View style={styles.fieldRow}>
                                <Field label="COMUNE:" value={comune} style={{ flex: 2 }} />
                                <Field label="PROV:" value={prov} style={{ flex: 0.6 }} />
                            </View>
                            <Field label="P. IVA / C.F:" value={piva} />
                            <Field label="CODICE SDI:" value={sdi} />
                            <Field label="MAIL / P.E.C:" value={pec} />
                        </>
                    );
                })()}

                {/* Base giuridica */}
                <BoxTitle>BASE GIURIDICA (PERCHE' E' LECITO IL TRATTAMENTO DEI DATI DEL CLIENTE)</BoxTitle>
                <Para>
                    Conferimento di incarico professionale per investigazione privata ai sensi degli artt. 134 e seguenti del T.u.l.p.s., del D.M. 269/2010 e del Regolamento GDPR - UE 2016/679, per il quale il trattamento dei dati delle persone interessate <Text style={{ fontFamily: 'Times-Bold' }}>è lecito</Text> ai sensi dei seguenti articoli:
                </Para>
                <Para bold>Art. 6 paragrafo 1 lettera B – Liceità del Trattamento <Text style={{ fontFamily: 'Times-Bold' }}>(riferito al cliente/mandante)</Text></Para>
                <Para>Il trattamento è necessario all'esecuzione di un contratto di cui l'interessato è parte o all'esecuzione di misure precontrattuali adottate su richiesta dello stesso</Para>
                <Para bold>Art. 6 paragrafo 1 lettera C – Liceità del Trattamento <Text style={{ fontFamily: 'Times-Bold' }}>(riferito al Titolare del Trattamento)</Text></Para>
                <Para>Il trattamento è necessario ad adempiere agli obblighi di legge da parte del Titolare del Trattamento.</Para>

                </View>
                {F(1)}
            </Page>

            {/* Pagina 2 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                <Para bold underline>La/Il sottoscritta/o MANDANTE ( sopra generalizzato ) dichiara:</Para>

                <Para>
                    <Text style={{ fontFamily: 'Times-Bold' }}>1. Di avere un legittimo interesse da tutelare</Text> e comunque di voler difendere/far valere un diritto in sede giudiziaria
                </Para>
                <Para>
                    <Text style={{ fontFamily: 'Times-Bold' }}>2. Di agire in nome e per conto proprio</Text> ovvero, laddove applicabile, in nome e per conto di figli minori sui quali esercita la responsabilità genitoriale o rispetto ai quali è legittimato alla rappresentanza, nonché, ove indicato, in nome e per conto di altri soggetti terzi, persone fisiche o giuridiche, enti o professionisti,
                    in qualità di titolare, legale rappresentante, amministratore, componente del Consiglio di Amministrazione, amministratore di sostegno, procuratore o soggetto formalmente delegato, in forza di idoneo titolo giuridico, procura o delega validamente conferita, nel rispetto della normativa vigente, assumendosene ogni conseguente responsabilità civile e penale;
                </Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>3. Di conoscere e comunque essere consapevole:</Text></Para>
                <View style={{ paddingLeft: 14, marginBottom: 2 }}>
                    <Para>• <Text style={{ fontFamily: 'Times-Bold' }}>delle conseguenze civili e penali</Text> cui incorre in caso di dichiarazioni mendaci (in tutto o in parte) e/o formazione o uso di atti falsi, con particolare riguardo a quelle attinenti la Sua dichiarata legittimità a chiedere l'esecuzione del suddetto servizio investigativo, e al soggetto sul quale ha chiesto di eseguire l'attività info-investigativa di cui è menzione,</Para>
                    <Para>• <Text style={{ fontFamily: 'Times-Bold' }}>della decadenza dagli eventuali benefici acquisiti</Text> in caso di non veridicità del contenuto delle dichiarazioni rese nel presente mandato,</Para>
                    <Para>• <Text style={{ fontFamily: 'Times-Bold' }}>dell'obbligo di utilizzare le informazioni e il materiale ricevuto esclusivamente per far valere o difendere un proprio diritto in sede giudiziaria</Text>,</Para>
                    <Para>• che – a insindacabile giudizio dell'istituto {nomeAzienda} - la richiesta deve rispettare i requisiti di liceità, correttezza, pertinenza e non eccedenza</Para>
                </View>

                <Para>Il Mandante affida incarico investigativo formale a <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text> (di seguito, anche "Agenzia" o "Istituto"), affinché svolga attività investigative finalizzate all'accertamento di fatti e circostanze rilevanti ai fini della tutela e/o difesa di un diritto, anche in sede giudiziaria.</Para>
                <Para>Le attività saranno svolte per il periodo strettamente necessario all'espletamento dell'incarico, salvo eventuali proroghe da concordarsi espressamente e in forma scritta tra le parti.</Para>

                <Para bold>
                    Premesso che, a conferma delle intese verbali intercorse, il Mandante conferisce incarico all'Agenzia di svolgere, per conto e nell'interesse dello stesso, attività investigative rientranti nel/i seguente/i ambito/i, da intendersi come cornice generale dell'incarico:
                </Para>

                <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 }}>
                    <Check checked={pratica?.ambito === 'privato'}          label="Ambito Privato" />
                    <Check checked={pratica?.ambito === 'aziendale'}        label="Ambito Aziendale" />
                    <Check checked={pratica?.ambito === 'commerciale'}      label="Ambito Commerciale" />
                    <Check checked={pratica?.ambito === 'assicurativo'}     label="Ambito Assicurativo" />
                </View>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8 }}>
                    <Check checked={pratica?.ambito === 'difensive'}        label="Ambito Indagini Difensive" />
                    <Check checked={pratica?.ambito === 'leggi_speciali'}   label="Ambito Leggi Speciali" />
                    <Check checked={pratica?.ambito === 'info_commerciali'} label="Informazioni Commerciali" />
                </View>

                    <BoxTitle>L'INCARICO HA PER OGGETTO LE SEGUENTI ATTIVITÀ:</BoxTitle>
                    <Para italic style={{ fontSize: 8.5 }}>[Riportare i dati identificativi del soggetto da investigare ed eventuali terzi coinvolti (se persone fisiche) nell'annesso prospetto.]</Para>
                    <TextBox value={pratica?.oggetto_attivita} minHeight={55} />

                    <BoxTitle>PRINCIPALI ELEMENTI DI FATTO CHE GIUSTIFICANO L'ATTIVITA' INVESTIGATIVA RICHIESTA:</BoxTitle>
                    <Para italic style={{ fontSize: 8.5 }}>[compilare solo se non già indicati o desumibili nel precedente campo "oggetto"]</Para>
                    <TextBox value={pratica?.elementi_fatto} minHeight={55} />
                </View>
                {F(2)}
            </Page>

            {/* Pagina 3 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                <Para bold underline>
                    Il Mandante precisa che il presente incarico è finalizzato alla tutela e all'accertamento di comportamenti illeciti o comunque scorretti che arrecano danno a se stesso e/o al soggetto rappresentato, al fine di poter esercitare o difendere un diritto giuridicamente rilevante, anche in sede giudiziale e/o stragiudiziale, nella fase propedeutica al giudizio, nel corso del procedimento e nella fase successiva allo stesso.
                </Para>
                <Para bold underline>
                    Il Mandante individua di seguito il diritto o l'interesse giuridicamente rilevante che costituisce presupposto essenziale e fondamento del presente incarico investigativo.
                </Para>

                <BoxTitle>INTERESSE O DIRITTO CHE SI INTENDE TUTELARE O ESERCITARE:</BoxTitle>
                <Para italic style={{ fontSize: 8.5 }}>[compilare solo se l'interesse/diritto non è già indicato o desumibile nel precedente campo "oggetto"]</Para>
                <TextBox value={pratica?.interesse_diritto_tutelato} minHeight={35} />

                <Para>A tal fine, il Mandante fornisce all'Agenzia, anche su separato supporto, tutte le informazioni e la documentazione di cui è a conoscenza, pertinenti e non eccedenti rispetto all'oggetto del presente incarico, dichiarando di averle acquisite legittimamente e restando unico responsabile della loro provenienza e utilizzabilità.</Para>

                <Para>Il Mandante indica altresì i recapiti utilizzabili per comunicazioni inerenti il presente mandato, anche di natura verbale, scritta o telematica, ivi incluse comunicazioni telefoniche, SMS, e-mail, invio e ricezione di documenti, nonché tramite applicazioni di messaggistica istantanea e piattaforme di comunicazione comunemente utilizzate quali, a titolo esemplificativo e non esaustivo, WhatsApp, Telegram e analoghi strumenti, comprese le comunicazioni di carattere amministrativo e le segnalazioni o comunicazioni urgenti, dichiarando di avere piena e legittima disponibilità dei recapiti indicati e impegnandosi a utilizzarne esclusivamente di propria titolarità o comunque nella sua piena disponibilità.</Para>
                <Para italic style={{ fontSize: 8.5 }}>si raccomanda di inserire esclusivamente recapiti di cui il Mandante/firmatario abbia piena disponibilità:</Para>

                {(() => {
                    // Recapiti del mandante: prima quelli inseriti nel wizard (pratica.*),
                    // poi l'anagrafica cliente (per le aziende i campi *Referente, poi i nativi).
                    const telMandante = pratica?.telefono
                        || (isAzienda ? (cliente?.telefonoReferente || cliente?.telefono) : cliente?.telefono);
                    // Email e PEC sono recapiti distinti: si mostrano entrambi se presenti.
                    const emailMandante = pratica?.email
                        || (isAzienda ? (cliente?.emailReferente || cliente?.email) : cliente?.email);
                    const pecMandante = pratica?.pec || cliente?.pec;
                    const emailPecMandante = [emailMandante, pecMandante ? `PEC: ${pecMandante}` : '']
                        .filter(Boolean).join(' – ');
                    return (
                        <>
                            <View>
                                <View style={[styles.fieldRow, { marginBottom: 0 }]}>
                                    <Text style={[styles.fieldLabel, { width: 70 }]}>TEL. MOBILE:</Text>
                                    <Text style={[styles.fieldValue, { marginBottom: 0 }]}>{telMandante || ''}</Text>
                                </View>
                                <Text style={{ fontSize: 7.5, color: C.midGray, fontFamily: 'Times-Italic', marginLeft: 74, marginTop: 2, marginBottom: 3 }}>utilizzabile anche per altre piattaforme e canali di comunicazione raggiungibili attraverso il numero.</Text>
                            </View>
                            <Field label="TEL. FISSO:" value={pratica?.telefono_fisso} />
                            <Field label="EMAIL / PEC:" value={emailPecMandante} />
                        </>
                    );
                })()}



                {/* Delegati – esattamente 2 slot */}
                <BoxTitle italic>
                    Altri soggetti che possono gestire i rapporti con {nomeAzienda}{'\n'}per conto: del Mandante
                </BoxTitle>
                {delegati.map((del: any, i: number) => (
                    <View key={i} style={{ marginBottom: 8 }}>
                        <Field label="NOME:" value={del.nome} />
                        <Field label="QUALIFICA:" value={del.qualifica} />
                        <View style={styles.fieldRow}>
                            <Field label="NATO/A A:" value={del.luogo_nascita} style={{ flex: 2 }} />
                            <Field label="IN DATA:" value={formatDate(del.data_nascita)} style={{ flex: 1 }} />
                        </View>
                        <Field label="RECAPITI:" value={
                            [del.telefono ? `Tel: ${del.telefono}` : '', del.email ? `Email: ${del.email}` : '']
                                .filter(Boolean).join(' – ')
                        } />
                    </View>
                ))}
                </View>
                {F(3)}
            </Page>

            {/* Pagina 4 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>
                {/* Condizioni generali */}
                <BoxTitle>CONDIZIONI GENERALI</BoxTitle>
                <View style={[styles.fieldRow, { alignItems: 'flex-end', marginBottom: 5 }]}>
                    <Text style={[styles.fieldLabel, { width: 230, fontFamily: 'Times-Roman' }]}>1.{'  '}Data e orario di inizio servizio:</Text>
                    <Text style={styles.fieldValue}>
                        {pratica?.data_inizio_servizio
                            ? `${formatDate(pratica.data_inizio_servizio)}${pratica.orario_inizio_servizio ? `  –  Ore ${pratica.orario_inizio_servizio}` : ''}`
                            : formatDate(pratica?.data_inizio)}
                    </Text>
                </View>
                <View style={[styles.fieldRow, { alignItems: 'flex-end', marginBottom: 2 }]}>
                    <Text style={[styles.fieldLabel, { width: 230, fontFamily: 'Times-Roman' }]}>2.{'  '}Termine ragionevole entro cui l'indagine deve essere conclusa (*):</Text>
                    <Text style={styles.fieldValue}>{pratica?.termine_servizio || pratica?.termine_indagine || ''}</Text>
                </View>
                <Para style={{ fontSize: 8.5 }}>(*) trattasi di termine indicativo necessario a garantire il rispetto dei principi imposti dalla normativa in tema di tutela dei dati personali che potrà in qualsiasi momento essere variato in caso di necessità.</Para>

                <SectionTitle>INFO ACCESSORIE – INFORMAZIONE PERIODICA E RAPPORTO DI FINE SERVIZIO</SectionTitle>
                <Para>Il Mandante verrà informato periodicamente dall'Istituto <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text> sull'andamento delle attività investigative, anche al fine di consentire una valutazione tempestiva delle determinazioni da adottare in ordine all'esercizio o alla difesa di un diritto in sede giudiziaria e/o al diritto alla prova.</Para>
                <Para>Il rapporto di fine servizio sarà redatto in forma strettamente funzionale all'oggetto del presente incarico e conterrà esclusivamente le informazioni e i dati pertinenti e rilevanti rispetto alle finalità dell'indagine, con esclusione di elementi superflui o non influenti.</Para>
                <Para>Il Mandante resta unico responsabile del corretto utilizzo delle risultanze investigative, che dovrà avvenire nel pieno rispetto della normativa vigente in materia di protezione dei dati personali (Regolamento UE 679/2016), impegnandosi in particolare al rispetto del vincolo di finalità, sancito dalla normativa, per il trattamento dei dati derivanti dall'indagine. per ogni successivo trattamento dei dati derivanti dall'attività investigativa.</Para>


                {/* 3. Condizioni economiche */}
                <SectionTitle>3. Condizioni economiche</SectionTitle>
                <Para>Il Mandante dichiara di aver preso integrale e preventiva visione del tariffario ufficiale di {nomeAzienda}, depositato e vigente alla data di sottoscrizione del presente mandato, e di accettarne senza riserva alcuna i contenuti, i criteri di calcolo e le modalità di applicazione dei compensi, riconoscendone piena validità ed efficacia ai fini della determinazione del corrispettivo dovuto. Il sottoscritto Mandante dichiara e si impegna a corrispondere e quietanzare il saldo dovuto per le attività investigative svolte alla conclusione delle indagini e/o al momento della consegna della relazione di servizio finale.</Para>

                <Para bold>3.1 COMPENSO concordato:</Para>
                <View style={{ paddingLeft: 10, marginBottom: 8 }}>
                    {/* Riga compatta: check + etichetta, importo e IVA. Niente sottolineatura
                        sotto il valore, si sovrapponeva a "+ IVA". */}
                    <View style={[styles.fieldRow, { alignItems: 'center', marginBottom: 6, flexWrap: 'nowrap' }]}>
                        <View style={{ width: 260, flexGrow: 0, flexShrink: 0 }}>
                            <Check checked={pratica?.tipo_compenso?.toLowerCase()?.trim() === 'corpo'} label="A CORPO" />
                        </View>
                        <Text style={{ fontSize: 9.5, fontFamily: 'Times-Bold', color: C.black, marginLeft: 4, minWidth: 90 }}>
                            {pratica?.tipo_compenso === 'corpo' && Number(pratica.importo_preventivato) > 0 ? `€ ${Number(pratica.importo_preventivato).toFixed(2)}` : (inBianco ? '€ ______________' : '—')}
                        </Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 6, flexShrink: 0 }]}>+ IVA</Text>
                    </View>
                    <View style={[styles.fieldRow, { alignItems: 'center', marginBottom: 0, flexWrap: 'nowrap' }]}>
                        <View style={{ width: 260, flexGrow: 0, flexShrink: 0 }}>
                            <Check checked={pratica?.tipo_compenso === 'consuntivo'} label="A CONSUNTIVO con BUDGET MASSIMO" />
                        </View>
                        <Text style={{ fontSize: 9.5, fontFamily: 'Times-Bold', color: C.black, marginLeft: 4, minWidth: 90 }}>
                            {pratica?.tipo_compenso === 'consuntivo' && Number(pratica.importo_preventivato) > 0 ? `€ ${Number(pratica.importo_preventivato).toFixed(2)}` : (inBianco ? '€ ______________' : '—')}
                        </Text>
                        <Text style={[styles.fieldLabel, { marginLeft: 6, flexShrink: 0 }]}>+ IVA</Text>
                    </View>
                    <Text style={{ fontSize: 7.5, color: C.midGray, fontFamily: 'Times-Italic', marginLeft: 22, marginTop: 1 }}>( da indicare solo se il Mandante vuole prevederlo )</Text>
                </View>

                <Para>secondo il seguente tariffario:</Para>
                <View style={styles.tariffeRow}>
                    <Text style={styles.tariffeLabel}>TARIFFA ORARIA FERIALE</Text>
                    <Text style={styles.tariffeValue}>€ {pratica?.tariffa_oraria_feriale || '___________'},00 + IVA</Text>
                </View>
                <View style={styles.tariffeRow}>
                    <Text style={styles.tariffeLabel}>TARIFFA ORARIA FESTIVA – NOTTURNA</Text>
                    <Text style={styles.tariffeValue}>€ {pratica?.tariffa_oraria_festiva || '___________'},00 + IVA</Text>
                </View>
                <View style={styles.tariffeRow}>
                    <Text style={styles.tariffeLabel}>TARIFFA GIORNALIERA</Text>
                    <Text style={styles.tariffeValue}>€ {pratica?.tariffa_giornaliera || '___________'},00 + IVA</Text>
                </View>
                <View style={styles.tariffeRow}>
                    <Text style={styles.tariffeLabel}>ACCORDI SPECIFICI (aggiungerli se previsti)</Text>
                    <Text style={styles.tariffeValue}>{pratica?.accordi_specifici || ''}</Text>
                </View>

                <Para bold style={{ marginTop: 8 }}>3.1.1. Autorizzazioni e disciplina comunicazioni fra le parti ( sms – whatsapp – email – telegram … ):</Para>
                <Para>
                    • Il Mandante autorizza sin d'ora l'esecuzione di un monte ore minimo di n.{' '}
                    <Text style={{ fontFamily: 'Times-Bold' }}>
                        {data.num_ore > 0 ? data.num_ore : '___________'}
                    </Text>{' '}
                    ore, che l'Agenzia potrà impiegare senza necessità di ulteriori conferme.
                </Para>
                <Para>• Il Mandante prende atto ed accetta espressamente che tutte le comunicazioni intercorse con l'Agenzia, effettuate mediante qualsiasi mezzo tracciabile, ivi comprese – a titolo esemplificativo e non esaustivo – e-mail (anche non PEC), messaggistica istantanea (WhatsApp, Telegram o analoghe), SMS, messaggi su piattaforme social, applicazioni digitali e ogni ulteriore canale elettronico o telematico utilizzato dalle parti, costituiscono comunicazioni valide tra le parti. Pertanto, eventuali istruzioni operative, autorizzazioni, conferme, modifiche dell'incarico, estensioni o riduzioni delle attività, variazioni del monte ore, approvazioni di spese, richiesta di servizi aggiuntivi, nonché ogni accordo operativo definito tramite i predetti canali, si intendono pienamente efficaci e vincolanti dal momento della loro ricezione e/o conferma, senza necessità di ulteriore formalizzazione.</Para>
                <Para>Il Mandante riconosce sin d'ora che tali comunicazioni potranno essere utilizzate e prodotte, ove necessario, come documentazione comprovante gli accordi intervenuti e le istruzioni impartite, anche ai fini della ricostruzione del rapporto e delle attività svolte.</Para>
                <Para>Resta inteso che l'eventuale mancata contestazione tempestiva di quanto comunicato equivarrà ad accettazione del relativo contenuto.</Para>
                </View>
                {F(4)}
            </Page>

            {/* Pagina 5 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                <Para bold>3.1.2. Il Mandante prende atto che:</Para>
                <Para>La quantificazione complessiva delle ore di attività investigativa non è predeterminabile al momento della sottoscrizione del presente mandato e dipenderà dall'evoluzione delle indagini e dalle esigenze operative che si manifesteranno nel corso delle stesse.</Para>
                <Para>Nel corso dell'esecuzione dell'incarico, il Mandante manterrà la facoltà di determinare, mediante proprie indicazioni impartite all'Agenzia, la prosecuzione dell'attività investigativa e l'entità dell'impegno operativo da svolgere, potendo autorizzare di volta in volta l'estensione, la riduzione o la sospensione delle ore di lavoro.</Para>
                <Para>Le ore di attività eseguite dall'Agenzia in conformità alle indicazioni del Mandante, nonché quelle rientranti nel monte ore minimo autorizzato, si intendono pienamente approvate e saranno computate ai fini della determinazione del compenso dovuto, secondo le tariffe pattuite.</Para>

                <NoteBox>
                    N.B.: Le tariffe indicate devono intendersi riferite a ciascun addetto impiegato nelle attività connesse e conseguenti al presente mandato, ivi comprese, a titolo esemplificativo e non esaustivo, le attività di back office, segreteria, coordinamento operativo e redazione della documentazione, e si applicano unitamente alle eventuali maggiorazioni previste dal tariffario ufficiale dell'Agenzia, quali, a titolo esemplificativo e non esaustivo, prestazioni notturne, festive o prefestive, diritti di urgenza, particolari condizioni operative, complessità dell'incarico, estensioni orarie o territoriali, secondo quanto previsto e disciplinato dal tariffario medesimo{'\n'}
                    cui verranno sommate le spese vive sostenute a piè di lista ed il rimborso per:
                </NoteBox>
                <View style={{ paddingLeft: 14, marginBottom: 5 }}>
                    <Para>• rimborso chilometrico per utilizzo mezzi di €/Km {data.rimborso_km || '___________'}.</Para>
                    <Para>• eventuali sanzioni amministrative conseguenti a violazioni al Codice della strada resesi inevitabili o necessarie nel corso delle attività investigative, anche pervenute successivamente alla conclusione delle indagini</Para>
                    <Para>• la perdita/danneggiamento di apparati di localizzazione satellitare o di sorveglianza eventualmente impiegati nelle attività di cui al presente mandato dall'Agenzia fino ad un massimo di € {data.apparati_localizzazione || '___________'},00 + IVA.</Para>
                </View>

                <Para bold>3.2 alla sottoscrizione del presente mandato investigativo, il Mandante versa:</Para>
                <View style={{ paddingLeft: 14, marginBottom: 5 }}>
                    <Para>• un deposito cauzionale per l'impiego di attrezzature particolari di € {data.cauzione ? data.cauzione.toFixed(2) : '__________,00'} + IVA. (localizzatori gps, o altri strumenti)</Para>
                    <Para>• Acconto sul compenso pari a € {data.acconto_compenso ? data.acconto_compenso.toFixed(2) : '__________,00'} + IVA.</Para>
                </View>

                <Para><Text style={{ fontFamily: 'Times-Bold' }}>3.3</Text> <Text style={{ fontFamily: 'Times-Bold' }}>Il Mandante riconoscerà all'Agenzia un compenso minimo riconosciuto,</Text> indipendentemente dalle attività svolte pari a € {data.acconto_compenso ? data.acconto_compenso.toFixed(2) : '______,00'} + IVA</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>3.4</Text> Qualora il Titolare e/o i collaboratori dell'Agenzia siano chiamati, su richiesta di una delle parti in causa e/o dell'Autorità Giudiziaria, a rendere testimonianza in qualità di testimoni in relazione alle attività svolte e alle risultanze del presente mandato, il Mandante prende atto e accetta di sostenere il pagamento degli onorari spettanti, determinati secondo il tariffario ufficiale dell'Agenzia, per ciascun soggetto coinvolto, nonché il rimborso di tutte le spese vive e accessorie sostenute.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>3.5</Text> Il deposito cauzionale sopra indicato verrà trattenuto dall'Agenzia nel caso in cui le attrezzature oggetto del deposito stesso andassero perse o fossero danneggiate nel corso delle attività di indagine e non per incuria o colpa del personale dell'Agenzia.</Para>


                <SectionTitle>3.6 RICOGNIZIONE DI DEBITO E PROMESSA DI PAGAMENTO (art. 1988 Codice Civile)</SectionTitle>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>Il sottoscritto Mandante dichiara e riconosce, ai sensi e per gli effetti dell'art. 1988 del Codice Civile, di riconoscere come dovuto in favore di <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text> il corrispettivo complessivamente maturato per l'incarico investigativo conferito con il presente mandato, quale risultante dall'applicazione delle condizioni economiche pattuite al paragrafo 3.</Text> In particolare, il Mandante dichiara di aver preso piena visione e di accettare integralmente quanto previsto:</Para>
                <View style={{ paddingLeft: 14, marginBottom: 4 }}>
                    <Para>• al paragrafo 3.1, con riferimento alla modalità di determinazione del compenso, sia "a corpo" sia "a consuntivo", sulla base del tariffario applicato;</Para>
                    <Para>• ai paragrafi 3.1.1 / 3.1.2, relativi alla stima del monte ore minimo, nonché alla successiva autorizzazione e gestione delle ore di attività investigative richieste nel corso dell'esecuzione dell'incarico;</Para>
                    <Para>• alla nota (N.B.) relativa all'applicazione delle tariffe per ciascun addetto impiegato, incluse le attività di back office, segreteria, coordinamento operativo e redazione documentale;</Para>
                    <Para>• ai paragrafi 3.2, 3.3, 3.4 e 3.5, concernenti deposito cauzionale, acconti, compenso minimo garantito, onorari per eventuali testimonianze e rimborso delle spese vive, accessorie e straordinarie.</Para>
                </View>
                </View>
                {F(5)}
            </Page>

            {/* Pagina 6 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>
                <Para>Il Mandante riconosce e accetta espressamente che l'importo finale del corrispettivo non è predeterminabile in via anticipata, in quanto direttamente dipendente dalle ore di attività investigative di volta in volta richieste e autorizzate dal Mandante stesso nel corso delle indagini, nonché dalle spese vive effettivamente sostenute dall'Agenzia per l'espletamento dell'incarico.</Para>
                <Para>Il Mandante dichiara e riconosce che le richieste e autorizzazioni relative alle ore di attività investigativa possono essere impartite anche verbalmente o mediante qualsiasi mezzo di comunicazione, ivi inclusi, a titolo meramente esemplificativo e non esaustivo, comunicazioni telefoniche, messaggistica istantanea (quali WhatsApp o analoghi), posta elettronica ordinaria o certificata, nonché nel corso di interlocuzioni intervenute durante lo svolgimento delle indagini, in funzione dell'andamento operativo delle stesse.</Para>
                <Para>Il Mandante riconosce altresì che le ore di attività svolte dall'Agenzia in conformità alle richieste, indicazioni e autorizzazioni impartite, nonché quelle rientranti nel monte ore minimo accettato, si intendono pienamente approvate, e che il relativo compenso costituisce credito certo, liquido e determinabile, sulla base dei criteri e delle tariffe previste dal presente mandato e dal tariffario applicato.</Para>
                <Para>Con la presente, il Mandante si impegna e promette irrevocabilmente di corrispondere all'Agenzia l'intero importo che risulterà dovuto in applicazione delle disposizioni economiche del presente mandato, nei termini e con le modalità ivi previste, rinunciando sin d'ora a sollevare contestazioni o eccezioni in ordine ai criteri di determinazione del compenso, alle tariffe applicate e alle ore di attività richieste, autorizzate ed eseguite.</Para>
                <Para bold>La presente dichiarazione costituisce ricognizione di debito e promessa di pagamento a tutti gli effetti di legge ed è valida quale prova scritta dell'obbligazione assunta dal Mandante.</Para>

                <Para bold>3.7 Ritardato pagamento</Para>
                <Para>Ove non diversamente pattuito per iscritto, il pagamento dell'indagine dovrà avvenire alla consegna della relazione di servizio finale. In caso di ritardo nel pagamento verranno applicati, ricorrendone i requisiti soggettivi ed oggettivi, gli interessi moratori nella misura prevista dal D.Lgs. n. 231/2002 e successive modifiche e integrazioni, con decorrenza dalla data di consegna della predetta relazione.</Para>
                <Para>L'eventuale concessione di una dilazione di pagamento costituisce mera facoltà dell'Agenzia e non diritto del Mandante; anche in caso di concessione della stessa, l'Agenzia si riserva la facoltà di revocarla in qualsiasi momento. In ogni caso di mancato rispetto delle scadenze pattuite, il Mandante perderà il beneficio del termine e sarà tenuto a corrispondere l'intero importo residuo a semplice richiesta dell'Agenzia, restando impregiudicato ogni ulteriore diritto dell'Istituto <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text>.</Para>
                <Para>Il corrispettivo dovuto dal Mandante per il presente incarico è determinato secondo le condizioni economiche pattuite tra le parti e riportate nel presente mandato, che contiene anche apposita ricognizione di debito. Il Mandante dichiara di aver preso piena conoscenza delle predette condizioni economiche, di aver letto e preso visione integrale del tariffario in uso presso l'Agenzia investigativa, nonché di accettarne integralmente il contenuto, e di assumere espressamente l'obbligazione di pagamento dei corrispettivi dovuti in favore dell'Agenzia, secondo quanto risultante dal presente atto. Resta inteso che l'atto di ricognizione di debito costituisce titolo autonomo ai sensi dell'art. 1988 c.c.</Para>



                {/* 4. Apparati */}
                <SectionTitle>4. Si concorda espressamente l'utilizzo nelle attività di:</SectionTitle>
                <View style={{ marginBottom: 4 }}>
                    <View style={[styles.fieldRow, { alignItems: 'center', marginBottom: 5 }]}>
                        <Check checked={pratica?.localizzazione_satellitare} label="SÌ" />
                        <Check checked={!inBianco && !pratica?.localizzazione_satellitare} label="NO" />
                        <Text style={[styles.fieldLabel, { marginLeft: 8 }]}>apparati di localizzazione satellitare</Text>
                    </View>
                    <View style={[styles.fieldRow, { alignItems: 'center', marginBottom: 5 }]}>
                        <Check checked={pratica?.ripresa} label="SÌ" />
                        <Check checked={!inBianco && !pratica?.ripresa} label="NO" />
                        <Text style={[styles.fieldLabel, { marginLeft: 8 }]}>apparati di ripresa e registrazione audio/video occultati</Text>
                    </View>
                    <View style={[styles.fieldRow, { alignItems: 'center' }]}>
                        <Check checked={!!data.testo_altro_apparati} label="SÌ" />
                        <Check checked={!inBianco && !data.testo_altro_apparati} label="NO" />
                        <Text style={[styles.fieldLabel, { marginLeft: 8 }]}>altro: </Text>
                        <Text style={styles.fieldValue}>{data.testo_altro_apparati || ''}</Text>
                    </View>
                </View>

                </View>
                {F(6)}
            </Page>

            {/* Pagina 7 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                    <BoxTitle>AUTORIZZAZIONE ALL'UTILIZZO DI APPARECCHIATURE DI LOCALIZZAZIONE SATELLITARE (GPS){'\n'}(ai sensi dell'art. 5 D.M. 269/2010)</BoxTitle>
                    <Para>Allo scopo di svolgere attività investigative finalizzate a far valere o difendere un diritto in sede giudiziaria, ai sensi e per gli effetti dell'art. 5 del D.M. 269/2010, il Mandante autorizza, sotto la propria esclusiva responsabilità, l'Istituto <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text> (di seguito anche "Agenzia") ad avvalersi di apparecchiature elettroniche di localizzazione satellitare <Text style={{ fontFamily: 'Times-Bold' }}>G.P.S. (Global Positioning System)</Text>, esclusivamente per il periodo strettamente necessario all'espletamento delle indagini oggetto del presente mandato.</Para>
                    <Para>Il Mandante autorizza altresì l'Agenzia ad installare tali apparecchiature elettroniche (GPS) esclusivamente sui veicoli indicati nel documento separato denominato <Text style={{ fontFamily: 'Times-Bold' }}>"Mandato – Annesso Dati Investigato"</Text>, alla voce <Text style={{ fontFamily: 'Times-Bold' }}>"Mezzi in Uso"</Text>, documento che costituisce parte integrante e sostanziale del presente incarico.</Para>
                    <Para>Il Mandante dichiara di essere edotto che l'attività di localizzazione verrà svolta nel rispetto della normativa vigente in materia di investigazioni private, tutela della riservatezza e protezione dei dati personali.</Para>
                    <Para>Il Mandante, consapevole delle responsabilità civili e penali previste dalla legge in caso di dichiarazioni mendaci, dichiara sotto la propria esclusiva responsabilità, a tutti gli effetti di legge, che il/i veicolo/i sul/i quale/i verrà installata l'apparecchiatura elettronica di localizzazione satellitare <Text style={{ fontFamily: 'Times-Bold' }}>è/sono di sua esclusiva proprietà</Text>, ovvero di proprietà del coniuge in regime di comunione dei beni ed acquistato/i successivamente alla data del matrimonio, ovvero di proprietà o nella disponibilità giuridica del Mandante in qualità di azienda ed assegnato/i in uso esclusivo e continuativo al soggetto investigato, anche a titolo esemplificativo e non esaustivo di veicolo aziendale, fringe benefit, leasing, noleggio a lungo termine o comodato, ovvero concesso/i in uso esclusivo dal Mandante a dipendenti, collaboratori, amministratori, soci o soggetti comunque legati da un rapporto giuridico rilevante, ovvero comunque nella disponibilità del Mandante, il quale dichiara di avere titolo legittimo a disporne e ad autorizzarne l'utilizzo ai fini investigativi anche qualora il/i veicolo/i risulti/no formalmente intestato/i a terzi.</Para>
                    <Para>Il Mandante <Text style={{ fontFamily: 'Times-Bold' }}>dichiara espressamente, in modo specifico e inequivoco</Text>, che il/i veicolo/i sopra indicato/i, <Text style={{ fontFamily: 'Times-Bold' }}>a prescindere dall'intestazione formale e dal titolo di proprietà</Text>, <Text style={{ fontFamily: 'Times-Bold' }}>è/sono stabilmente, continuativamente e in via esclusiva nella piena disponibilità e nell'uso del soggetto</Text> sul quale viene richiesta <Text style={{ fontFamily: 'Times-Bold' }}>l'attività investigativa</Text>, ossia del soggetto espressamente indicato nel <Text style={{ fontFamily: 'Times-Bold' }}>"Mandato – Annesso Dati Investigato"</Text>, e che nessun altro soggetto ne fa uso abituale o concorrente durante il periodo oggetto di indagine.</Para>
                    <Para>Il Mandante dichiara altresì di essere pienamente legittimato ad autorizzare l'installazione e l'utilizzo delle suddette apparecchiature ai fini investigativi, assumendosi integralmente ogni responsabilità in ordine alla veridicità delle dichiarazioni rese.</Para>
                    <Para>Il Mandante esonera sin d'ora l'Agenzia <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text> da ogni responsabilità civile e penale derivante da dichiarazioni non veritiere, inesatte o incomplete in ordine alla titolarità, disponibilità o utilizzo del/i veicolo/i indicato/i, restando espressamente inteso che l'Agenzia opera esclusivamente sulla base delle dichiarazioni rese dal Mandante e delle informazioni contenute nel <Text style={{ fontFamily: 'Times-Bold' }}>"Mandato – Annesso Dati Investigato"</Text>, senza obbligo di verifica della titolarità o della disponibilità giuridica dei veicoli.</Para>

                    {/* 5. Esclusività */}
                    <SectionTitle>5. Esclusività dell'incarico</SectionTitle>
                    <Para>Il Mandante si impegna a non conferire ad altri istituti o agenzie investigative incarichi aventi ad oggetto attività di indagine sul medesimo soggetto o sui medesimi fatti, in concomitanza con lo svolgimento del presente mandato, salvo preventivo accordo scritto dell'Agenzia.</Para>
                    <Para><Text style={{ fontFamily: 'Times-Bold' }}>5.1 Il Mandante, consapevole delle responsabilità civili e penali</Text> previste dalla legge, si impegna a trattare con la massima riservatezza tutte le informazioni, notizie, documenti e risultanze investigative ricevute dall'Agenzia in esecuzione del presente mandato, nel rispetto della normativa vigente in materia di protezione dei dati personali, delle disposizioni sul segreto professionale e della rigorosa osservanza delle leggi applicabili, con particolare riferimento al T.U.L.P.S.</Para>
                    <Para><Text style={{ fontFamily: 'Times-Bold' }}>5.2 Il Mandante prende atto e accetta che, in caso di:</Text></Para>
                    <View style={{ paddingLeft: 14, marginBottom: 4 }}>
                        <Para>a) inosservanza delle basilari regole di discrezione e riservatezza relative al presente mandato e/o ai risultati dell'attività investigativa;</Para>
                        <Para>b) svolgimento diretto o indiretto di contro-indagini, anche per il tramite di parenti, amici o conoscenti, finalizzate a controllare o interferire con l'operato dell'Agenzia;</Para>
                        <Para>c) azioni, omissioni o divulgazioni idonee a compromettere l'esito delle indagini, i risultati da esse derivanti, ovvero a determinare l'interruzione dell'attività investigativa, esponendo anche a rischi l'incolumità degli operatori dell'Agenzia;</Para>
                    </View>
                    <Para>lo stesso sarà tenuto al pagamento integrale del corrispettivo pattuito e ne risponderà nelle sedi competenti, restando impregiudicato ogni ulteriore diritto dell'Agenzia.</Para>

                </View>
                {F(7)}
            </Page>

            {/* Pagina 8 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                {/* 6. Responsabilità del mandante */}
                <SectionTitle>6 Responsabilità del mandante - Con la sottoscrizione del presente documento il Mandante:</SectionTitle>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>6.1</Text> <Text style={{ fontFamily: 'Times-Bold' }}>si impegna a comunicare all'Agenzia ogni elemento utile al corretto svolgimento del presente mandato,</Text> nonché tutte le informazioni di cui sia a conoscenza, pertinenti e non eccedenti rispetto alle finalità del servizio richiesto, dichiarando di esserne legittimamente in possesso e restando responsabile della loro provenienza e utilizzabilità.</Para>
                <Para>In particolare, il Mandante ha l'obbligo di informare l'Agenzia qualora abbia conferito, anche in precedenza, incarichi ad altri istituti investigativi aventi ad oggetto il controllo del medesimo soggetto e se quest'ultimo sia già stato oggetto di osservazione e/o pedinamento.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>si impegna altresì a informare tempestivamente l'Agenzia in merito a qualsiasi variazione delle informazioni già fornite</Text>, nonché a ogni circostanza sopravvenuta utile ai fini del corretto svolgimento dell'attività investigativa e/o della valutazione circa l'eventuale sospensione della stessa, anche per motivi di sicurezza, opportunità o tutela del servizio.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>6.2</Text> si assume ogni responsabilità circa la veridicità delle affermazioni riportate all'Agenzia sulle finalità dell'indagine oggetto del mandato e sul successivo utilizzo delle sue risultanze, che avverrà nel pieno rispetto della normativa in tema di tutela dei dati personali (Reg. UE 2016/679 e D. Lgs. 196/2003 e s.m.i.);</Para>
                <Para>A tal proposito il Mandante dichiara di essere a conoscenza del vincolo di finalità per i trattamenti dei dati derivanti dall'indagine e non provenienti da fonti pubbliche (ossia i pubblici registri, gli elenchi, gli atti o i documenti conoscibili da chiunque in base alla vigente normativa) che potranno essere effettuati senza un esplicito consenso degli interessati, ed in pochissimi casi senza una informativa ex art. 14 Reg. UE 2016/679, solamente per <Text style={{ fontFamily: 'Times-Bold' }}>"far valere o difendere in sede giudiziaria un proprio diritto,"</Text> <Text style={{ fontFamily: 'Times-Bold' }}>"ciò, sia per i dati trattati nel corso di un procedimento(anche in sede amministrativa, di arbitrato o di conciliazione), sia nella fase propedeutica all'instaurazione di un eventuale giudizio, anche al fine di verificare con le parti se vi sia un diritto da tutelare utilmente in sede giudiziaria, sia nella fase successiva alla risoluzione, giudiziale o stragiudiziale della lite"</Text>.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>6.3</Text> si impegna a garantire il più assoluto riserbo in ordine ai rapporti informativi intercorsi con l'Agenzia, nonché ai colloqui, diretti o telefonici, avuti con la stessa.</Para>
                <Para>Il Mandante prende altresì atto ed accetta:{'\n'}
                    a) dell'assoluto divieto di rivelare a chiunque, senza preventiva autorizzazione dell'Agenzia, l'esistenza delle indagini in corso, le attività svolte o comunque i servizi espletati dall'Agenzia;{'\n'}
                    b) che i nominativi dei collaboratori interni dell'Agenzia, nonché di eventuali investigatori privati, professionisti o aziende esterne incaricati, anche in outsourcing, dell'esecuzione di parti delle attività investigative, costituiscono informazioni riservate e non potranno in alcun modo essere divulgati a terzi.
                </Para>
                <Para>In caso di violazione degli obblighi di riservatezza di cui alla presente clausola, fatto salvo il diritto dell'Agenzia di recedere dal contratto e di ottenere il pagamento del compenso per le attività già prestate, il Mandante sarà altresì tenuto a risarcire integralmente ogni danno, patrimoniale e non patrimoniale, conseguente alla violazione stessa.</Para>

                <Para bold>6.4 Nomina a responsabile ex art. 28 Reg. UE 2016/679 – NEL CASO DI MANDATI CONFERITI DA AZIENDE, ENTI, PROFESSIONISTI</Para>
                <View style={[styles.fieldRow, { alignItems: 'center', marginBottom: 8 }]}>
                    <Check checked={pratica?.nomina_responsabile === 'si'} label="SÌ" />
                    {/* NO anche se il campo è vuoto (mandati salvati prima della scelta). */}
                    <Check checked={!inBianco && pratica?.nomina_responsabile !== 'si'} label="NO" />
                </View>
                <Para>In considerazione del fatto che l'oggetto del presente mandato riguarda attività investigative, adempimenti a obblighi legali, normativi o contrattuali del Mandante, nonché attività di controllo che comportano trattamenti di dati personali che devono restare sotto lo stretto controllo del Mandante, con la sottoscrizione del presente mandato, <Text style={{ fontFamily: 'Times-Bold' }}>laddove selezionata l'opzione "SÌ"</Text>, il Mandante procede a nominare l'Agenzia <Text style={{ fontFamily: 'Times-Bold' }}>Responsabile del trattamento ai sensi dell'art. 28 del Regolamento (UE) 2016/679</Text>, esclusivamente con riferimento ai trattamenti di dati personali strettamente necessari alla predisposizione, organizzazione ed esecuzione dei Servizi così come definiti nel presente mandato. Resta espressamente inteso che la presente nomina non si estende a trattamenti diversi o ulteriori rispetto a quelli sopra indicati e non produce effetti al di fuori dell'ambito e delle finalità del presente incarico.</Para>
                <Para>In ottemperanza a quanto prescritto dalla vigente normativa in materia di protezione dei dati personali, l'Agenzia, in qualità di Responsabile del trattamento, sarà tenuta a: a) individuare, nell'ambito della propria struttura organizzativa, i soggetti autorizzati al trattamento dei dati personali, i quali saranno tenuti a operare nel rispetto della massima riservatezza in relazione a dati, informazioni e a qualsiasi elemento di cui vengano a conoscenza nello svolgimento delle attività loro affidate; b) segnalare al Mandante, senza ingiustificato ritardo, ogni violazione dei dati personali (sospetta o accertata) riguardante dati di cui il Mandante è Titolare del trattamento, fornendo ogni elemento utile e necessario ai fini della valutazione e gestione dell'evento, nonché dell'eventuale notifica all'Autorità Garante e, ove richiesto, agli interessati; c) assistere il Mandante, limitatamente ai trattamenti effettuati in esecuzione del presente mandato, nel garantire il rispetto dei diritti riconosciuti agli interessati dalla normativa vigente in materia di protezione dei dati personali; d) previo accordo in ordine ad eventuali costi aggiuntivi, attenersi alle istruzioni documentate del Mandante e soddisfare eventuali richieste dello stesso, informandolo tempestivamente qualora, a proprio motivato parere, tali istruzioni risultino in contrasto con la normativa vigente in materia di protezione dei dati personali.</Para>


                </View>
                {F(8)}
            </Page>

            {/* Pagina 9 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>

                <Para>L'Agenzia potrà agire in piena autonomia per quanto concerne la scelta, l'organizzazione e la gestione dei criteri, degli strumenti e delle modalità operative con cui svolgere la propria attività, effettuare i trattamenti di dati personali ed adottare le misure di sicurezza ritenute idonee.</Para>
                <Para>A tal fine, l'Agenzia potrà delegare, in tutto o in parte, le fasi del trattamento che riterrà opportune a soggetti terzi propri fornitori, che gestiscono o erogano servizi funzionali e/o ausiliari alle attività oggetto del presente mandato (quali, a titolo esemplificativo e non esaustivo, servizi di manutenzione e gestione dei sistemi informatici, agenzie sub-mandatarie, consulenti tecnici o professionisti esterni), ai quali potrà conferire nomina a Responsabile del trattamento ai sensi dell'art. 28 del Regolamento (UE) 2016/679, i quali saranno a loro volta tenuti a garantire il pieno rispetto della normativa vigente in materia di protezione dei dati personali e delle condizioni previste dal presente documento.</Para>
                <Para>In caso di ricorso a tale facoltà, l'Agenzia ne darà comunicazione al Mandante e provvederà ad adempiere a quanto previsto, nel presente mandato, e quanto specificato successivamente al punto 13.5.</Para>
                <Para>Ove eventuali istruzioni del Mandante comportassero variazioni significative nelle scelte organizzative, tecniche o operative dell'Agenzia, quest'ultima si riserva la facoltà di rinunciare all'incarico ovvero di richiedere una congrua compensazione per gli eventuali maggiori costi sostenuti in conseguenza delle richieste del Mandante stesso.</Para>
                <Para>Non rientrano nella presente nomina attribuzioni e responsabilità in ordine all'esattezza, all'aggiornamento e alla completezza dei dati personali trattati e trasmessi dal Mandante all'Agenzia ai fini dell'esecuzione del presente mandato, restando tali responsabilità integralmente in capo al Mandante stesso, così come l'adempimento degli ulteriori obblighi non espressamente indicati nel presente documento e imposti dalla normativa vigente in materia di protezione dei dati personali, ivi inclusi, a titolo esemplificativo e non esaustivo, gli obblighi di informativa agli interessati ai sensi degli artt. 13 e 14 del Regolamento (UE) 2016/679 e l'eventuale acquisizione del consenso degli stessi, ove richiesto dalla legge.</Para>
                <Para>La presente nomina avrà la medesima durata ed efficacia del presente mandato; al termine dello stesso, i dati personali trattati dall'Agenzia, la cui ulteriore conservazione non sia imposta da obblighi di legge o giustificata da altra legittima finalità prevista dalla normativa vigente, saranno, su istruzione del Titolare del trattamento, cancellati o restituiti, fatti salvi i casi di conservazione obbligatoria previsti dalla legge.</Para>

                {/* 7. Responsabilità dell'Agenzia */}
                <SectionTitle>7 Responsabilità dell'Agenzia</SectionTitle>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>7.1</Text> Per l'esecuzione dei Servizi l'Agenzia si avvarrà di risorse e figure professionali adeguate, proprie e/o di soggetti terzi, selezionate secondo criteri di competenza e professionalità, nel rispetto della normativa vigente.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>7.2</Text> L'Agenzia è tenuta a:{'\n'}
                    a) trattare le informazioni e i dati personali ricevuti esclusivamente per l'esecuzione del presente mandato, salvo specifici consensi o altre legittime basi giuridiche previste dalla normativa vigente, nel pieno rispetto delle disposizioni in materia di protezione dei dati personali di cui al D.Lgs. 196/2003 e s.m.i. e al Regolamento (UE) 2016/679, operando nel rispetto della massima riservatezza in relazione a qualsiasi dato o informazione trattata;{'\n'}
                    b) fornire al Mandante informazioni e risultanze investigative utilizzabili per le finalità dallo stesso dichiarate, in quanto acquisite nel rispetto della normativa vigente e delle disposizioni applicabili all'attività investigativa autorizzata.
                </Para>



                {/* 8. Differimento */}
                <SectionTitle>8 Differimento dei termini</SectionTitle>
                <Para>In caso di malattia, infortunio o altro legittimo impedimento che coinvolga l'Agenzia e/o i suoi collaboratori, i termini previsti per lo svolgimento delle attività investigative si intenderanno automaticamente prorogati per un periodo corrispondente alla durata dell'impedimento, restando in ogni caso esclusa l'applicazione di penali o responsabilità a carico dell'Agenzia per il ritardo conseguente.</Para>

                </View>
                {F(9)}
            </Page>

            {/* Pagina 10 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>
                {/* 9. Consegna delle risultanze */}
                <SectionTitle>9 Consegna delle risultanze</SectionTitle>
                <Para>Le risultanze delle attività di cui al presente mandato, previamente condivise e valutate unitamente al Mandante, verranno riportate in apposito "Rapporto di Conclusione Indagini", redatto in forma scritta e consegnato in unica copia previa sottoscrizione di verbale di conclusione dell'attività investigativa.</Para>
                <Para>Il Mandante prende atto ed accetta espressamente che la richiesta di redazione e consegna del Rapporto di Conclusione Indagini dovrà essere formulata nell'immediatezza della conclusione delle operazioni investigative e, comunque, non oltre il termine perentorio di 5 (cinque) giorni dalla data di cessazione dell'attività.</Para>
                <Para>Decorso tale termine, l'Agenzia provvederà alla cancellazione e/o distruzione del materiale raccolto (documentazione fotografica, riprese video, appunti operativi, tracciati, supporti digitali ed ogni altro dato personale trattato), in ottemperanza agli obblighi previsti dalla normativa vigente in materia di protezione dei dati personali (Regolamento UE 2016/679 – GDPR, D.Lgs. 196/2003 e successive modifiche), nonché secondo i principi di minimizzazione, limitazione della conservazione e proporzionalità del trattamento.</Para>
                <Para>Conseguentemente, decorso il suddetto termine, l'Agenzia non potrà più essere in grado di redigere il Rapporto di Conclusione Indagini né di fornire copia della documentazione investigativa, per intervenuta distruzione dei dati.</Para>
                <Para>Resta in ogni caso impregiudicata la piena validità e disponibilità della testimonianza dell'Istituto investigativo e dei propri operatori autorizzati, che potrà essere resa in qualsiasi momento nelle sedi competenti, secondo quanto previsto dalla normativa vigente.</Para>

                {/* 10. Conservazione */}
                <SectionTitle>10 Conservazione</SectionTitle>
                <Para>Salvo particolari esigenze, l'Agenzia non conserverà i dati risultanti dalle attività investigative svolte e provvederà alla loro consegna al Mandante in unica copia. Il Rapporto di conclusione indagine sarà redatto sulla base delle informazioni già precedentemente anticipate al Mandante nel corso delle indagini e da quest'ultimo valutate anche in relazione alla loro pertinenza e non eccedenza rispetto alla finalità dichiarata nel presente mandato. A tal proposito, il Mandante si impegna formalmente a mettere a disposizione dell'Agenzia, qualora la stessa sia chiamata a dimostrare la liceità e la correttezza del proprio operato, a semplice richiesta, l'intero materiale investigativo ricevuto. Ciò premesso, il Mandante autorizza l'Agenzia a trattenere, ove se ne presentasse la necessità, i dati e le informazioni raccolte per gli scopi previsti dalla legge e a utilizzarli, in qualsiasi forma, fino al termine dell'indagine e/o dei suoi eventuali sviluppi, siano essi fotografie, riprese video, registrazioni audio o audio-telefoniche e ogni altro materiale necessario al completamento delle attività investigative o alla tutela, in ogni sede futura, dei diritti dell'Agenzia.</Para>

                {/* 11. Disdetta */}
                <SectionTitle>11 Disdetta</SectionTitle>
                <Para>L'eventuale disdetta del presente mandato dovrà essere comunicata per iscritto entro e non oltre 24 (ventiquattro) ore dalla data del suo conferimento; decorso tale termine, il Mandante sarà tenuto a corrispondere all'Agenzia il compenso minimo riconosciuto, come sopra indicato. Resta espressamente inteso che non è ammessa la disdetta del mandato nei casi in cui il servizio sia qualificato come urgente ovvero qualora sia stato concordato l'inizio delle attività investigative entro le 24 (ventiquattro) ore dal conferimento dell'incarico.</Para>

                {/* 12. Diritto di recesso */}
                <SectionTitle>12 Diritto di recesso / risoluzione</SectionTitle>
                <Para>Qualora, nel corso delle attività investigative, si accerti che il loro svolgimento comporti difficoltà oggettivamente sproporzionate ovvero risulti impossibile per cause non imputabili all'Agenzia, la stessa avrà facoltà di risolvere il presente mandato, senza obbligo di fornire ulteriori motivazioni.</Para>
                <Para>In tale ipotesi, resta fermo il diritto dell'Agenzia al pagamento del corrispettivo maturato per le attività effettivamente svolte fino a quel momento. È in ogni caso escluso qualsiasi diritto del Mandante al risarcimento del danno, il quale rinuncia sin d'ora a sollevare eccezioni o contestazioni per asserito inadempimento dell'Agenzia in relazione alla risoluzione del mandato nei casi sopra indicati.</Para>

                {/* 13. Clausole finali */}
                <SectionTitle>13. Clausole finali</SectionTitle>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>13.1</Text> Il Mandante prende atto e riconosce che l'obbligazione assunta dall'Agenzia nell'esecuzione del presente mandato è un'obbligazione di mezzi e non di risultato.</Para>
                <Para>Fatto salvo il caso di dolo o colpa grave, l'eventuale responsabilità dell'Agenzia, anche per ritardi, errori, omissioni, inadempienze operative, negligenze, anche lievi, o altre condotte colpose poste in essere dall'Agenzia stessa, dai suoi collaboratori e/o corrispondenti nell'esecuzione del presente incarico, è in ogni caso limitata al risarcimento del danno entro il limite massimo pari all'importo del corrispettivo del servizio effettivamente pagato dal Mandante all'Agenzia.</Para>

                </View>
                {F(10)}
            </Page>

            {/* Pagina 11 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>
                    <Para>Resta in ogni caso esclusa la risarcibilità di danni ulteriori, indiretti, consequenziali, riflessi o comunque non immediatamente e direttamente riconducibili all'esecuzione del presente mandato.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>13.2</Text> L'Agenzia non risponderà di fronte ad un'eventuale richiesta di risarcimento del danno conseguenti a prove insufficienti, non utili o non producibili in giudizio, ove tali circostanze non siano attribuibili a colpa grave dell'Agenzia stessa.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>13.3</Text> La violazione, anche parziale, da parte del Mandante di una o più delle condizioni previste dal presente mandato costituisce grave inadempimento contrattuale e comporta la risoluzione di diritto del contratto, fatto salvo il diritto dell'Agenzia di agire per il risarcimento di tutti i danni subiti. Il Mandante sarà pertanto tenuto a risarcire integralmente ogni danno arrecato all'Agenzia, ai suoi collaboratori e/o a terzi coinvolti, a qualsiasi titolo, nelle attività investigative. Il risarcimento dei danni sarà determinato tenendo conto, tra l'altro, della gravità della violazione, della rilevanza e natura delle informazioni indebitamente divulgate, dell'eventuale compromissione della sicurezza e dell'incolumità degli operatori dell'Agenzia derivante dalla rivelazione della loro identità o anonimato, nonché dell'eventuale identificazione delle attrezzature, dei mezzi e dei veicoli utilizzati nello svolgimento delle attività investigative.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>13.4</Text> Il Mandante autorizza l'Agenzia a contattarlo, ove necessario, ai recapiti indicati nel presente mandato (quali, a titolo esemplificativo e non esaustivo, telefono fisso, telefono mobile, SMS, e-mail, fax), in qualsiasi fase dell'incarico, per esigenze connesse all'espletamento dell'attività investigativa, agli aspetti organizzativi e amministrativi ad essa collegati, nonché per il perseguimento dei legittimi interessi dell'Agenzia, ivi compresi, a titolo esemplificativo, quelli relativi alla fatturazione, all'incasso dei corrispettivi, alle comunicazioni e alle eventuali notifiche.</Para>
                <Para><Text style={{ fontFamily: 'Times-Bold' }}>13.5</Text> Il Mandante autorizza sin d'ora l'Agenzia ad avvalersi, a proprio insindacabile giudizio e senza necessità di ulteriore autorizzazione, di collaboratori esterni, investigatori privati, consulenti tecnici, professionisti, istituti corrispondenti e/o soggetti terzi, operanti sia sul territorio nazionale sia all'estero, qualora ciò si renda necessario o opportuno ai fini dell'espletamento, dell'organizzazione e della migliore esecuzione del presente mandato. Il ricorso a tali soggetti potrà avvenire anche per singole fasi o attività dell'indagine, restando inteso che l'Agenzia conserverà il coordinamento e la responsabilità dell'incarico nei limiti previsti dal presente mandato e dalla normativa vigente. I soggetti ai quali verranno eventualmente affidate alcune attività potranno essere indicati nominativamente anche in momenti successivi alla sottoscrizione del presente mandato, mediante annotazione in calce allo stesso o con separata comunicazione, senza che ciò comporti modifica o integrazione sostanziale del rapporto contrattuale.</Para>


                {/* 14. Foro */}
                <SectionTitle>14 Legge applicabile e Foro competente</SectionTitle>
                <Para>Il presente Contratto è governato dalla legge italiana. Per qualunque controversia relativa al presente mandato sarà competente il Foro territorialmente competente per la sede legale di <Text style={{ fontFamily: 'Times-Bold' }}>{nomeAzienda}</Text>.</Para>

                {/* 15. Dati personali */}
                <SectionTitle>15 Tutela dei dati personali</SectionTitle>
                <Para>Il Mandante, in qualità di Persona Fisica, dichiara di essere stato informato in modo completo e conforme agli artt. 13 e 14 del Regolamento (UE) 2016/679 al momento del conferimento del presente mandato, in ordine alle modalità, alle finalità e alle basi giuridiche del trattamento dei propri dati personali.</Para>
                <Para>In particolare, il Mandante dichiara di essere stato reso edotto del fatto che, nell'ambito dell'esecuzione del presente incarico, potranno essere oggetto di trattamento anche dati personali appartenenti a categorie particolari ai sensi dell'art. 9 del Regolamento (UE) 2016/679, nonché dati relativi a condanne penali e reati di cui all'art. 10 del medesimo Regolamento, nei limiti strettamente necessari allo svolgimento delle attività investigative e alle finalità dichiarate nel presente mandato. Il Mandante prende altresì atto che tali trattamenti sono effettuati senza necessità di un consenso esplicito, in quanto fondati sulle basi giuridiche previste dalla normativa vigente, ed in particolare sulla necessità di accertare, esercitare o difendere un diritto in sede giudiziaria, ai sensi dell'art. 9, par. 2, lett. f), e dell'art. 10 del Regolamento (UE) 2016/679.</Para>
                <Para>Il Mandante dichiara inoltre di essere informato e consapevole del fatto che, ove ciò si rendesse necessario in relazione alle finalità del presente mandato, i dati personali potranno essere trasferiti anche verso Paesi situati al di fuori dell'Unione Europea, esclusivamente nei casi e nei limiti consentiti dalla normativa vigente, ed in particolare ai sensi dell'art. 49, par. 1, lett. e), del Regolamento (UE) 2016/679, qualora il trasferimento risulti strettamente necessario per l'accertamento, l'esercizio o la difesa di un diritto in sede giudiziaria.</Para>

                {/* Prima firma: l'ancora #otp_m1# sta in basso a destra nel riquadro,
                    dove andrà la firma vera. Riquadro largo il 60% della pagina. */}
                <Text style={styles.signatureLabel}>Il Mandante</Text>
                <View style={{ marginBottom: 8, width: '60%', alignSelf: 'flex-start' }} wrap={false}>
                    <View style={styles.signatureArrowRow}>
                        <Text style={styles.signatureArrow}>{'>'}</Text>
                        <View style={[styles.signatureLine, { height: 42, justifyContent: 'flex-end', alignItems: 'flex-end', paddingRight: 6, paddingBottom: 4 }]}>
                            <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{'#otp_m1#'}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.divider} />

                </View>
                {F(11)}
            </Page>

            {/* Pagina 12 */}
            <Page size="A4" style={styles.page}>
                <PageHeader settings={safeSettings} data={data} />
                <Text fixed style={styles.footerPageNum}
                      render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                <View style={cs}>
                <Para bold>Il Mandante, sin d'ora e per lo svolgimento delle attività investigative oggetto del presente mandato, autorizza l'Istituto <Text style={{ fontFamily: 'Times-BoldItalic' }}>{nomeAzienda}</Text>, in persona del Titolare, ad avvalersi, oltre che dei propri collaboratori C.I.I.E., quali soggetti terzi incaricati del trattamento dei dati personali in ragione di specifiche competenze, formazione ed esperienza professionale, anche di ulteriori investigatori privati regolarmente autorizzati ai sensi della normativa vigente.</Para>

                <Para>Tali soggetti, eventualmente incaricati anche per singole fasi o specifiche attività dell'indagine, saranno designati contestualmente o anche in momenti successivi, nel rispetto delle disposizioni di legge applicabili, e opereranno sotto il coordinamento e la direzione dell'Agenzia, nei limiti delle rispettive autorizzazioni e competenze. Resta inteso che i predetti collaboratori, investigatori privati e soggetti terzi saranno vincolati al rispetto degli obblighi di riservatezza, segreto professionale e protezione dei dati personali, nonché alle condizioni previste dal presente mandato e dalla normativa vigente, con particolare riferimento al T.U.L.P.S. e al Regolamento (UE) 2016/679.</Para>

                {/* Righe per gli investigatori esterni delegati: le prime si riempiono con
                    investigatori_esterni (una riga per nome), le altre restano vuote da compilare a mano. */}
                {(() => {
                    const raw = (data?.investigatori_esterni || pratica?.investigatori_esterni || '').toString();
                    const nomi = raw.split(/\r?\n/).map((s: string) => s.trim()).filter(Boolean);
                    return (
                        <View style={{ marginVertical: 3 }}>
                            {[0, 1, 2, 3, 4].map(i => (
                                <View key={i} style={{ borderBottomWidth: 0.5, borderBottomColor: C.silver, marginVertical: 2, minHeight: 11 }}>
                                    {nomi[i] && (
                                        <Text style={{ fontSize: 9, fontFamily: 'Times-Bold', color: C.black, paddingBottom: 1, lineHeight: 1.1 }}>
                                            {nomi[i]}
                                        </Text>
                                    )}
                                </View>
                            ))}
                        </View>
                    );
                })()}

                <Para>Il Mandante autorizza altresì l'eventuale designazione nel prosieguo, qualora l'Istituto oggi incaricato ne ravvisi discrezionalmente l'opportunità, previa indicazione del relativo nominativo, conferendo agli stessi la nomina di Responsabile esterno del trattamento. Con riferimento all'esecuzione dell'incarico e al trattamento di dati sensibili, valgono le disposizioni impartite dal Garante per la protezione dei dati personali.</Para>


                {/* Allegati */}
                <BoxTitle>ALLEGATI AL PRESENTE MANDATO</BoxTitle>
                <View style={styles.allegatiBox}>
                    <Text style={styles.allegatiItem}>PROSPETTO CONTENENTE DATI PERSONALI RELATIVI AL SOGGETTO INVESTIGATO</Text>
                    <Text style={styles.allegatiItemLast}>INFORMATIVA MANDANTI</Text>
                </View>

                {/* Clausole 1341/1342 */}
                <View style={styles.clausoleBox}>
                    <Text style={[styles.clausoleText, { fontFamily: 'Times-Roman' }]}>
                        Ai sensi e per gli effetti degli artt. 1341 e 1342 c.c., il Mandante dichiara di aver attentamente letto il presente contratto e di approvare espressamente le seguenti clausole: 3 "Condizioni economiche"; 3.1 "Compenso concordato"; 3.1.1 "Autorizzazioni e disciplina delle comunicazioni fra le parti"; 3.1.2 "Quantificazione delle ore e approvazione delle attività"; 3.6 "Ricognizione di debito e promessa di pagamento"; 3.7 "Ritardato pagamento"; 4 "Utilizzo di apparecchiature tecniche e localizzazione satellitare"; 5 "Esclusività dell'incarico"; 6 "Responsabilità del Mandante", con riguardo ai punti 6.1, 6.2, 6.3 e 6.4 ove applicabile; 8 "Differimento dei termini"; 9 "Consegna delle risultanze"; 10 "Conservazione"; 11 "Disdetta"; 12 "Diritto di recesso / risoluzione"; 13 "Clausole finali", con riguardo ai punti 13.1, 13.2, 13.3 e 13.5; 14 "Legge applicabile e foro competente".
                    </Text>
                </View>

                {/* Seconda firma, come la prima. */}
                <Text style={styles.signatureLabel}>Il Mandante</Text>
                    <View style={{ marginBottom: 8, width: '60%', alignSelf: 'flex-start' }} wrap={false}>
                    <View style={styles.signatureArrowRow}>
                        <Text style={styles.signatureArrow}>{'>'}</Text>
                        <View style={[styles.signatureLine, { height: 42, justifyContent: 'flex-end', alignItems: 'flex-end', paddingRight: 6, paddingBottom: 4 }]}>
                            <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{'#otp_m2#'}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.divider} />

                {/* Box DPR 445: bordo blu come le altre clausole, il rosso è solo per i riquadri firma. */}
                <View style={[styles.clausoleBox, { backgroundColor: '#f0f4fa', borderColor: C.sectionBorder }]}>
                    <Text style={[styles.clausoleText, { fontFamily: 'Times-Roman' }]}>
                        Il Mandante, consapevole delle responsabilità e delle sanzioni previste dal D.P.R. 28/12/2000 n. 445, per le false attestazioni e le mendaci dichiarazioni, dichiara formalmente che tutto quanto da egli riferito sul presente mandato risponde al vero, in particolare in riferimento a: oggetto dell'investigazione, principali elementi di fatto che la giustificano, interesse o diritto che si intende tutelare o esercitare e gli altri elementi comprovanti la legittimità delle proprie richieste
                    </Text>
                </View>

                {/* Terza firma, come le precedenti. */}
                <Text style={styles.signatureLabel}>Il Mandante</Text>
                    <View style={{ marginBottom: 8, width: '60%', alignSelf: 'flex-start' }} wrap={false}>
                    <View style={[styles.signatureArrowRow, { justifyContent: 'center' }]}>
                        <Text style={styles.signatureArrow}>{'>'}</Text>
                        <View style={[styles.signatureLine, { flex: 1, height: 42, justifyContent: 'flex-end', alignItems: 'flex-end', paddingRight: 6, paddingBottom: 4 }]}>
                            <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{'#otp_m3#'}</Text>
                        </View>
                    </View>
                </View>

                </View>
                {F(12)}
            </Page>
        </Document>
    );
};

export default MandatoPDF;